import axios from 'axios';
import type {
  Direction,
  Hand,
  OrderMode,
  ProgressPolicy,
  RhythmDensity,
  ScaleCell,
  ScaleConfig,
} from 'Types/ExerciseContentTypes';

/**
 * The exercise read: `GET /api/v1/exercises/:slug`. The client resolves an exercise by its slug
 * and never carries a database id of its own: the id, the levels with their cells and tiers, each
 * tier's package key and waypoint, and the two progress policies all come from the server, so
 * every environment plays the exercise it holds, whatever id it minted.
 *
 * Levels come ordered by `level_number`, a permanent authoring identity; the order they are shown
 * in comes from the scale catalog, never from that number.
 */

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

/** A progress policy as the server stores it: `n` and `m` are null where the type has none. */
export interface ServerProgressPolicy {
  type: string;
  n: number | null;
  m: number | null;
}

/** A tier's waypoint; a dimension the exercise's type does not have is null. */
export interface ExerciseContentWaypoint {
  hand: Hand | null;
  range_octaves: number | null;
  direction: Direction | null;
  rhythm_density: RhythmDensity | null;
  tempo: number | null;
  /** Gauntlets only: the order a gauntlet over the level serves its cells in. */
  order_mode: OrderMode | null;
}

export interface ExerciseContentTier {
  tier_number: number;
  package_key: string;
  waypoint: ExerciseContentWaypoint;
}

export interface ExerciseContentLevel {
  level_number: number;
  title: string;
  is_review: boolean;
  /** The level's (scale, tonic) cells, in content order. */
  cells: ScaleCell[];
  /** By tier number. */
  tiers: ExerciseContentTier[];
}

export interface ExerciseContent {
  id: number;
  slug: string;
  title: string;
  exercise_type: string;
  bundling: string;
  predictability: string;
  clearance_policy: ServerProgressPolicy;
  mastery_policy: ServerProgressPolicy;
  levels: ExerciseContentLevel[];
}

/** GET one exercise by slug. Rejects on any failure (a 404 for a slug the server does not hold). */
export async function getExerciseBySlug(slug: string): Promise<ExerciseContent> {
  const res = await axios.get(`${baseUrl}exercises/${encodeURIComponent(slug)}`);
  return res.data as ExerciseContent;
}

/** The playable configuration a waypoint names, or null when it leaves a scale dimension unset. */
export function waypointConfig(waypoint: ExerciseContentWaypoint): ScaleConfig | null {
  const { hand, range_octaves, direction, rhythm_density, tempo } = waypoint;
  return hand !== null &&
    range_octaves !== null &&
    direction !== null &&
    rhythm_density !== null &&
    tempo !== null
    ? { hand, range_octaves, direction, rhythm_density, tempo }
    : null;
}

const isCount = (value: number | null): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

/**
 * The server's policy as the session engine reads it, or null when the server sends a policy this
 * client does not know (then the exercise is not played: guessing a bar would count takes the
 * server does not).
 */
export function toProgressPolicy(policy: ServerProgressPolicy): ProgressPolicy | null {
  switch (policy.type) {
    case 'single':
      return { type: 'single' };
    case 'cumulative':
      return isCount(policy.n) ? { type: 'cumulative', n: policy.n } : null;
    case 'streak':
      return isCount(policy.n) ? { type: 'streak', n: policy.n } : null;
    case 'x_of_y':
      return isCount(policy.n) && isCount(policy.m) && policy.n <= policy.m
        ? { type: 'x_of_y', n: policy.n, m: policy.m }
        : null;
    default:
      return null;
  }
}
